import { DEFAULT_SESSION_QUERY_SCOPE_TOKEN } from "~/lib/query-keys";
import type { AssetUniverseCanonicalModel } from "./models";
import { toAssetUniverseCanonicalId } from "./models";
import {
  getPendingCanonicalEquityQuotes,
  getPendingCanonicalMarketQuotes,
  readPendingCanonicalEquityQuotes,
  readPendingCanonicalMarketQuotes,
} from "./asset-domain-session-state";
import type {
  AssetUniverseCanonicalEquityQuoteSeedRow,
  AssetUniverseCanonicalMarketQuoteSeedRow,
  BrowserAssetDomainCollectionOptions,
  PendingCanonicalEquityQuote,
  PendingCanonicalMarketQuote,
} from "./collection-types";
import {
  getAssetDomainCollections,
  type AssetDomainCollections,
} from "./collection-registry";
import type { LocalCollectionLike } from "./collection-sync";

export async function seedAssetUniverseCanonicalMarketQuotes(
  quotes: readonly AssetUniverseCanonicalMarketQuoteSeedRow[],
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  if (quotes.length === 0) {
    return [];
  }

  const sessionScopeToken =
    options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const collections = getAssetDomainCollections({
    sessionScopeToken,
  });
  const updatedAt = toSeedUpdatedAt(options?.marketQuoteUpdatedAt);
  const quoteByCoinId = new Map(
    quotes.flatMap((quote) => {
      const coinId = normalizeCoingeckoId(quote.id);
      return coinId ? [[coinId, quote] as const] : [];
    }),
  );
  if (quoteByCoinId.size === 0) {
    return [];
  }

  const pendingQuotes = getPendingCanonicalMarketQuotes(sessionScopeToken);
  for (const [coinId, quote] of quoteByCoinId) {
    pendingQuotes.set(
      coinId,
      mergePendingCanonicalMarketQuote(pendingQuotes.get(coinId), {
        quote,
        updatedAt,
      }),
    );
  }

  return await applyPendingCanonicalMarketQuotes({
    collections,
    sessionScopeToken,
  });
}

export async function applyPendingCanonicalMarketQuotes(input: {
  collections: AssetDomainCollections;
  sessionScopeToken: string;
}): Promise<string[]> {
  const pendingQuotes = readPendingCanonicalMarketQuotes(
    input.sessionScopeToken,
  );
  if (!pendingQuotes || pendingQuotes.size === 0) {
    return [];
  }

  const canonicalIdsByCoinId = mapAssetCanonicalIdsByCoingeckoId(
    input.collections,
  );
  const nextRows: Array<{
    assetCanonicalId: string;
    coinId: string;
    quote: AssetUniverseCanonicalMarketQuoteSeedRow;
    rowId: string;
    updatedAt: number;
  }> = [];

  const currentCanonicalRows = input.collections.assetUniverseCanonical.state;
  for (const [coinId, pending] of pendingQuotes) {
    const assetCanonicalIds = canonicalIdsByCoinId.get(coinId) ?? [];
    for (const assetCanonicalId of assetCanonicalIds) {
      const rowId = toAssetUniverseCanonicalId(assetCanonicalId);
      if (!currentCanonicalRows.has(rowId)) {
        continue;
      }

      nextRows.push({
        assetCanonicalId,
        coinId,
        quote: pending.quote,
        rowId,
        updatedAt: pending.updatedAt,
      });
    }
  }

  if (nextRows.length === 0) {
    return [];
  }

  await syncCanonicalMarketQuoteRows(
    input.collections.assetUniverseCanonical,
    nextRows,
  );

  return nextRows.map((row) => row.assetCanonicalId);
}

/**
 * Seed underlying-market quotes for equity and fund canonicals, keyed by
 * ticker. The CoinGecko path above is deliberately restricted to crypto
 * canonicals even when an equity wrapper product has a CoinGecko id, so an
 * issuer token can never replace the underlying stock's Massive quote.
 * Quotes for canonicals not yet in the collection stay pending and land
 * when their identity row arrives.
 */
export async function seedAssetUniverseCanonicalEquityQuotes(
  quotes: readonly AssetUniverseCanonicalEquityQuoteSeedRow[],
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  if (quotes.length === 0) {
    return [];
  }

  const sessionScopeToken =
    options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const collections = getAssetDomainCollections({
    sessionScopeToken,
  });
  const updatedAt = toSeedUpdatedAt(options?.marketQuoteUpdatedAt);
  const pendingQuotes = getPendingCanonicalEquityQuotes(sessionScopeToken);
  for (const quote of quotes) {
    const ticker = normalizeEquityTicker(quote.ticker);
    if (!ticker) {
      continue;
    }
    pendingQuotes.set(
      ticker,
      mergePendingCanonicalEquityQuote(pendingQuotes.get(ticker), {
        quote: { ...quote, ticker },
        updatedAt,
      }),
    );
  }

  return await applyPendingCanonicalEquityQuotes({
    collections,
    sessionScopeToken,
  });
}

export async function applyPendingCanonicalEquityQuotes(input: {
  collections: AssetDomainCollections;
  sessionScopeToken: string;
}): Promise<string[]> {
  const pendingQuotes = readPendingCanonicalEquityQuotes(
    input.sessionScopeToken,
  );
  if (!pendingQuotes || pendingQuotes.size === 0) {
    return [];
  }

  const canonicalIdsByTicker = mapEquityCanonicalIdsByTicker(input.collections);
  const nextRows: Array<{
    assetCanonicalId: string;
    quote: AssetUniverseCanonicalEquityQuoteSeedRow;
    rowId: string;
    updatedAt: number;
  }> = [];

  const currentCanonicalRows = input.collections.assetUniverseCanonical.state;
  for (const [ticker, pending] of pendingQuotes) {
    for (const assetCanonicalId of canonicalIdsByTicker.get(ticker) ?? []) {
      const rowId = toAssetUniverseCanonicalId(assetCanonicalId);
      if (!currentCanonicalRows.has(rowId)) {
        continue;
      }

      nextRows.push({
        assetCanonicalId,
        quote: pending.quote,
        rowId,
        updatedAt: pending.updatedAt,
      });
    }
  }

  if (nextRows.length === 0) {
    return [];
  }

  await syncCanonicalEquityQuoteRows(
    input.collections.assetUniverseCanonical,
    nextRows,
  );

  return nextRows.map((row) => row.assetCanonicalId);
}

function mergePendingCanonicalEquityQuote(
  existing: PendingCanonicalEquityQuote | undefined,
  incoming: PendingCanonicalEquityQuote,
): PendingCanonicalEquityQuote {
  if (!existing || incoming.updatedAt >= existing.updatedAt) {
    const quote: AssetUniverseCanonicalEquityQuoteSeedRow = {
      ...existing?.quote,
      ticker: incoming.quote.ticker,
    };
    if (incoming.quote.marketCapUsd !== undefined) {
      quote.marketCapUsd = incoming.quote.marketCapUsd;
    }
    if (incoming.quote.priceChangePercent1h !== undefined) {
      quote.priceChangePercent1h = incoming.quote.priceChangePercent1h;
    }
    if (incoming.quote.priceChangePercent24h !== undefined) {
      quote.priceChangePercent24h = incoming.quote.priceChangePercent24h;
    }
    if (incoming.quote.priceUsd !== undefined) {
      quote.priceUsd = incoming.quote.priceUsd;
    }
    if (incoming.quote.totalVolumeUsd !== undefined) {
      quote.totalVolumeUsd = incoming.quote.totalVolumeUsd;
    }

    return {
      quote,
      updatedAt: incoming.updatedAt,
    };
  }

  return existing;
}

function mapEquityCanonicalIdsByTicker(
  collections: AssetDomainCollections,
): Map<string, string[]> {
  const mapped = new Map<string, string[]>();

  function append(ticker: string | null, assetCanonicalId: string) {
    if (!ticker) {
      return;
    }

    const existing = mapped.get(ticker);
    if (existing) {
      if (!existing.includes(assetCanonicalId)) {
        existing.push(assetCanonicalId);
      }
      return;
    }

    mapped.set(ticker, [assetCanonicalId]);
  }

  for (const row of collections.assetUniverseCanonical.state.values()) {
    if (isEquityOrFundAssetClass(row.assetClass)) {
      append(normalizeEquityTicker(row.symbol), row.assetCanonicalId);
    }
  }

  for (const link of collections.assetUniverseSymbolLinks.state.values()) {
    if (isEquityOrFundAssetClass(link.assetClass)) {
      append(normalizeEquityTicker(link.symbol), link.assetCanonicalId);
    }
  }

  return mapped;
}

function isEquityOrFundAssetClass(
  assetClass: string | null | undefined,
): boolean {
  const normalized = assetClass?.trim().toLowerCase();
  return normalized === "equity" || normalized === "fund";
}

function normalizeEquityTicker(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim().toUpperCase();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

async function syncCanonicalEquityQuoteRows(
  collection: LocalCollectionLike<AssetUniverseCanonicalModel>,
  rows: readonly {
    assetCanonicalId: string;
    quote: AssetUniverseCanonicalEquityQuoteSeedRow;
    rowId: string;
    updatedAt: number;
  }[],
): Promise<void> {
  const nextRowsById = new Map(rows.map((row) => [row.rowId, row] as const));
  const currentRows = collection.state;
  const rowIds = [...nextRowsById.keys()].filter((rowId) =>
    currentRows.has(rowId),
  );
  if (rowIds.length === 0) {
    return;
  }

  await collection.update(rowIds, (drafts) => {
    for (let index = 0; index < drafts.length; index += 1) {
      const draft = drafts[index];
      const nextRow = rowIds[index]
        ? nextRowsById.get(rowIds[index]!)
        : undefined;
      if (!draft || !nextRow) {
        continue;
      }

      Object.assign(
        draft,
        mergeCanonicalEquityQuoteRow({
          existing: draft,
          quote: nextRow.quote,
          updatedAt: nextRow.updatedAt,
        }),
      );
    }
  }).isPersisted.promise;
}

// Same freshness rule as the CoinGecko merge: an older quote never
// overwrites a newer one. Identity fields and the CoinGecko id are untouched;
// `marketQuoteUpdatedAt` is what keeps identity re-seeds from clearing the
// merged values (see mergeAssetUniverseCanonicalSeedRow).
function mergeCanonicalEquityQuoteRow(input: {
  existing: AssetUniverseCanonicalModel;
  quote: AssetUniverseCanonicalEquityQuoteSeedRow;
  updatedAt: number;
}): AssetUniverseCanonicalModel {
  const existingMarketQuoteUpdatedAt = readMetadataNumber(
    input.existing.metadata.marketQuoteUpdatedAt,
  );
  if (
    existingMarketQuoteUpdatedAt !== null &&
    input.updatedAt < existingMarketQuoteUpdatedAt
  ) {
    return input.existing;
  }

  return {
    ...input.existing,
    marketCapUsd:
      input.quote.marketCapUsd === undefined
        ? input.existing.marketCapUsd
        : input.quote.marketCapUsd,
    metadata: {
      ...input.existing.metadata,
      marketQuoteUpdatedAt: input.updatedAt,
    },
    priceChangePercent1h:
      input.quote.priceChangePercent1h === undefined
        ? input.existing.priceChangePercent1h
        : input.quote.priceChangePercent1h,
    priceChangePercent24h:
      input.quote.priceChangePercent24h === undefined
        ? input.existing.priceChangePercent24h
        : input.quote.priceChangePercent24h,
    priceUsd:
      input.quote.priceUsd === undefined
        ? input.existing.priceUsd
        : input.quote.priceUsd,
    totalVolumeUsd:
      input.quote.totalVolumeUsd === undefined
        ? input.existing.totalVolumeUsd
        : input.quote.totalVolumeUsd,
    updatedAt: Math.max(input.existing.updatedAt, input.updatedAt),
  };
}

function mergePendingCanonicalMarketQuote(
  existing: PendingCanonicalMarketQuote | undefined,
  incoming: PendingCanonicalMarketQuote,
): PendingCanonicalMarketQuote {
  if (!existing || incoming.updatedAt >= existing.updatedAt) {
    return {
      quote: mergeCanonicalMarketQuoteSeedValues(
        existing?.quote,
        incoming.quote,
      ),
      updatedAt: incoming.updatedAt,
    };
  }

  return existing;
}

function mergeCanonicalMarketQuoteSeedValues(
  existing: AssetUniverseCanonicalMarketQuoteSeedRow | undefined,
  incoming: AssetUniverseCanonicalMarketQuoteSeedRow,
): AssetUniverseCanonicalMarketQuoteSeedRow {
  if (!existing) {
    return incoming;
  }

  return {
    ...incoming,
    image: mergeMarketQuoteImage(existing.image, incoming.image),
    marketCapRank: incoming.marketCapRank ?? existing.marketCapRank,
    marketCapUsd: incoming.marketCapUsd ?? existing.marketCapUsd,
    priceChangePercent1h:
      incoming.priceChangePercent1h ?? existing.priceChangePercent1h,
    priceChangePercent24h:
      incoming.priceChangePercent24h ?? existing.priceChangePercent24h,
    priceUsd: incoming.priceUsd ?? existing.priceUsd,
    totalValueLockedUsd:
      incoming.totalValueLockedUsd ?? existing.totalValueLockedUsd ?? null,
    totalVolumeUsd: incoming.totalVolumeUsd ?? existing.totalVolumeUsd,
  };
}

function mergeMarketQuoteImage(
  existing: AssetUniverseCanonicalMarketQuoteSeedRow["image"],
  incoming: AssetUniverseCanonicalMarketQuoteSeedRow["image"],
): AssetUniverseCanonicalMarketQuoteSeedRow["image"] {
  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  return {
    large: incoming.large ?? existing.large,
    small: incoming.small ?? existing.small,
    thumb: incoming.thumb ?? existing.thumb,
  };
}

function toSeedUpdatedAt(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : Date.now();
}

async function syncCanonicalMarketQuoteRows(
  collection: LocalCollectionLike<AssetUniverseCanonicalModel>,
  rows: readonly {
    assetCanonicalId: string;
    coinId: string;
    quote: AssetUniverseCanonicalMarketQuoteSeedRow;
    rowId: string;
    updatedAt: number;
  }[],
): Promise<void> {
  const nextRowsById = new Map(rows.map((row) => [row.rowId, row] as const));
  const currentRows = collection.state;
  const rowIds = [...nextRowsById.keys()].filter((rowId) =>
    currentRows.has(rowId),
  );
  if (rowIds.length === 0) {
    return;
  }

  await collection.update(rowIds, (drafts) => {
    for (let index = 0; index < drafts.length; index += 1) {
      const draft = drafts[index];
      const nextRow = rowIds[index]
        ? nextRowsById.get(rowIds[index]!)
        : undefined;
      if (!draft || !nextRow) {
        continue;
      }

      Object.assign(
        draft,
        mergeCanonicalMarketQuoteRow({
          coinId: nextRow.coinId,
          existing: draft,
          quote: nextRow.quote,
          updatedAt: nextRow.updatedAt,
        }),
      );
    }
  }).isPersisted.promise;
}

function mapAssetCanonicalIdsByCoingeckoId(
  collections: AssetDomainCollections,
): Map<string, string[]> {
  const mapped = new Map<string, string[]>();
  const cryptoCanonicalIds = new Set(
    [...collections.assetUniverseCanonical.state.values()].flatMap((row) =>
      row.assetClass?.trim().toLowerCase() === "crypto"
        ? [row.assetCanonicalId]
        : [],
    ),
  );

  function append(coinId: string | null, assetCanonicalId: string) {
    const normalizedCoinId = normalizeCoingeckoId(coinId);
    if (!normalizedCoinId) {
      return;
    }

    const existing = mapped.get(normalizedCoinId);
    if (existing) {
      if (!existing.includes(assetCanonicalId)) {
        existing.push(assetCanonicalId);
      }
      return;
    }

    mapped.set(normalizedCoinId, [assetCanonicalId]);
  }

  for (const row of collections.assetUniverseCanonical.state.values()) {
    if (!cryptoCanonicalIds.has(row.assetCanonicalId)) {
      continue;
    }
    append(row.coingeckoId, row.assetCanonicalId);
    append(
      readMetadataCoingeckoId(row.metadata, "coingeckoId"),
      row.assetCanonicalId,
    );
  }

  for (const product of collections.assetUniverseProducts.state.values()) {
    if (!cryptoCanonicalIds.has(product.assetCanonicalId)) {
      continue;
    }
    append(
      readMetadataCoingeckoId(product.metadata, "canonicalCoingeckoId"),
      product.assetCanonicalId,
    );
    append(
      readMetadataCoingeckoId(product.metadata, "coingeckoId"),
      product.assetCanonicalId,
    );
    append(
      product.source.toLowerCase() === "coingecko"
        ? product.sourceProductId
        : null,
      product.assetCanonicalId,
    );
  }

  return mapped;
}

function mergeCanonicalMarketQuoteRow(input: {
  coinId: string;
  existing: AssetUniverseCanonicalModel;
  quote: AssetUniverseCanonicalMarketQuoteSeedRow;
  updatedAt: number;
}): AssetUniverseCanonicalModel {
  const existingMarketQuoteUpdatedAt = readMetadataNumber(
    input.existing.metadata.marketQuoteUpdatedAt,
  );
  if (
    existingMarketQuoteUpdatedAt !== null &&
    input.updatedAt < existingMarketQuoteUpdatedAt
  ) {
    return input.existing;
  }

  const imageUrl = getMarketQuoteImageUrl(input.quote);
  const coingeckoId = input.existing.coingeckoId ?? input.coinId;
  const shouldTrackMarketQuote =
    hasMarketQuoteValue(input.quote) ||
    hasCanonicalMarketQuoteData(input.existing);

  return {
    ...input.existing,
    coingeckoId,
    imageUrl: imageUrl ?? input.existing.imageUrl,
    marketCapRank: input.quote.marketCapRank ?? input.existing.marketCapRank,
    marketCapUsd: input.quote.marketCapUsd ?? input.existing.marketCapUsd,
    metadata: {
      ...input.existing.metadata,
      coingeckoId,
      imageLargeUrl:
        input.quote.image?.large ??
        readMetadataString(input.existing.metadata.imageLargeUrl),
      imageSmallUrl:
        input.quote.image?.small ??
        readMetadataString(input.existing.metadata.imageSmallUrl),
      imageThumbUrl:
        input.quote.image?.thumb ??
        readMetadataString(input.existing.metadata.imageThumbUrl),
      marketCapRank:
        input.quote.marketCapRank ??
        input.existing.marketCapRank ??
        readMetadataNumber(input.existing.metadata.marketCapRank),
      marketQuoteUpdatedAt: shouldTrackMarketQuote
        ? input.updatedAt
        : existingMarketQuoteUpdatedAt,
      totalValueLockedUsd:
        input.quote.totalValueLockedUsd ??
        input.existing.totalValueLockedUsd ??
        readMetadataNumber(input.existing.metadata.totalValueLockedUsd),
      totalVolumeUsd:
        input.quote.totalVolumeUsd ??
        input.existing.totalVolumeUsd ??
        readMetadataNumber(input.existing.metadata.totalVolumeUsd),
    },
    priceChangePercent1h:
      input.quote.priceChangePercent1h ?? input.existing.priceChangePercent1h,
    priceChangePercent24h:
      input.quote.priceChangePercent24h ?? input.existing.priceChangePercent24h,
    priceUsd: input.quote.priceUsd ?? input.existing.priceUsd,
    totalValueLockedUsd:
      input.quote.totalValueLockedUsd ?? input.existing.totalValueLockedUsd,
    totalVolumeUsd: input.quote.totalVolumeUsd ?? input.existing.totalVolumeUsd,
    updatedAt: Math.max(input.existing.updatedAt, input.updatedAt),
  };
}

function hasMarketQuoteValue(
  quote: AssetUniverseCanonicalMarketQuoteSeedRow,
): boolean {
  return (
    getMarketQuoteImageUrl(quote) !== null ||
    quote.marketCapRank !== null ||
    quote.marketCapUsd !== null ||
    quote.priceChangePercent1h !== null ||
    quote.priceChangePercent24h !== null ||
    quote.priceUsd !== null ||
    (quote.totalValueLockedUsd !== null &&
      quote.totalValueLockedUsd !== undefined) ||
    quote.totalVolumeUsd !== null
  );
}

function getMarketQuoteImageUrl(
  quote: AssetUniverseCanonicalMarketQuoteSeedRow,
): string | null {
  return quote.image?.large ?? quote.image?.small ?? quote.image?.thumb ?? null;
}

function readMetadataCoingeckoId(
  metadata: Record<string, unknown>,
  key: string,
): string | null {
  return normalizeCoingeckoId(readMetadataString(metadata[key]));
}

function normalizeCoingeckoId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/^coingecko:/i, "").toLowerCase();
}

function readMetadataNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function readMetadataString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

export function mergeAssetUniverseCanonicalSeedRow(
  existing: AssetUniverseCanonicalModel | undefined,
  incoming: AssetUniverseCanonicalModel,
): AssetUniverseCanonicalModel {
  if (!existing) {
    return incoming;
  }

  const preferExistingMarketData = hasCanonicalMarketQuoteData(existing);
  const coingeckoId = incoming.coingeckoId ?? existing.coingeckoId;

  return {
    ...existing,
    ...incoming,
    coingeckoId,
    imageUrl: preferExistingMarketData
      ? (existing.imageUrl ?? incoming.imageUrl)
      : (incoming.imageUrl ?? existing.imageUrl),
    marketCapRank: preferExistingMarketData
      ? existing.marketCapRank
      : (incoming.marketCapRank ?? existing.marketCapRank),
    marketCapUsd: preferExistingMarketData
      ? existing.marketCapUsd
      : (incoming.marketCapUsd ?? existing.marketCapUsd),
    metadata: mergeCanonicalMetadata({
      coingeckoId,
      existing,
      incoming,
      preferExistingMarketData,
    }),
    priceChangePercent1h: preferExistingMarketData
      ? existing.priceChangePercent1h
      : (incoming.priceChangePercent1h ?? existing.priceChangePercent1h),
    priceChangePercent24h: preferExistingMarketData
      ? existing.priceChangePercent24h
      : (incoming.priceChangePercent24h ?? existing.priceChangePercent24h),
    priceUsd: preferExistingMarketData
      ? existing.priceUsd
      : (incoming.priceUsd ?? existing.priceUsd),
    streamSymbolUnique:
      incoming.streamSymbolUnique ?? existing.streamSymbolUnique,
    totalValueLockedUsd: preferExistingMarketData
      ? existing.totalValueLockedUsd
      : (incoming.totalValueLockedUsd ?? existing.totalValueLockedUsd),
    totalVolumeUsd: preferExistingMarketData
      ? existing.totalVolumeUsd
      : (incoming.totalVolumeUsd ?? existing.totalVolumeUsd),
    updatedAt: Math.max(existing.updatedAt, incoming.updatedAt),
  };
}

function hasCanonicalMarketQuoteData(
  row: AssetUniverseCanonicalModel,
): boolean {
  return (
    readMetadataNumber(row.metadata.marketQuoteUpdatedAt) !== null ||
    row.priceChangePercent1h !== null ||
    row.totalValueLockedUsd !== null ||
    row.totalVolumeUsd !== null ||
    readMetadataString(row.metadata.imageLargeUrl) !== null ||
    readMetadataString(row.metadata.imageSmallUrl) !== null ||
    readMetadataString(row.metadata.imageThumbUrl) !== null
  );
}

function mergeCanonicalMetadata(input: {
  coingeckoId: string | null;
  existing: AssetUniverseCanonicalModel;
  incoming: AssetUniverseCanonicalModel;
  preferExistingMarketData: boolean;
}): Record<string, unknown> {
  const metadata = {
    ...input.existing.metadata,
    ...input.incoming.metadata,
  };

  if (input.coingeckoId) {
    metadata.coingeckoId = input.coingeckoId;
  }

  if (!input.preferExistingMarketData) {
    return metadata;
  }

  metadata.imageLargeUrl =
    readMetadataString(input.existing.metadata.imageLargeUrl) ??
    readMetadataString(input.incoming.metadata.imageLargeUrl);
  metadata.imageSmallUrl =
    readMetadataString(input.existing.metadata.imageSmallUrl) ??
    readMetadataString(input.incoming.metadata.imageSmallUrl);
  metadata.imageThumbUrl =
    readMetadataString(input.existing.metadata.imageThumbUrl) ??
    readMetadataString(input.incoming.metadata.imageThumbUrl);
  metadata.marketCapRank =
    input.existing.marketCapRank ??
    readMetadataNumber(input.existing.metadata.marketCapRank);
  metadata.totalValueLockedUsd =
    input.existing.totalValueLockedUsd ??
    readMetadataNumber(input.existing.metadata.totalValueLockedUsd);
  metadata.totalVolumeUsd =
    input.existing.totalVolumeUsd ??
    readMetadataNumber(input.existing.metadata.totalVolumeUsd);

  return metadata;
}
